import PropTypes from 'prop-types';
import React, {
  memo,
  Fragment,
  useMemo,
  Children,
  useState,
  useRef,
  useCallback
} from 'react';
import { withTheme } from 'styled-components';
import { Pie, Arc } from '@visx/shape';
import { Group } from '@visx/group';
import {
  StyledDonutChartSvg,
  StyledDonutChartText,
  StyledDonutChartWrapper
} from './DonutChart.styles';
import { useWindowSize } from '../../utils';
import uniqid from 'uniqid';

const defaultMargin = { top: 20, left: 20, right: 20, bottom: 20 };

const DonutChart = ({
  donutThickness,
  margin = defaultMargin,
  data,
  color,
  children
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const windowSize = useWindowSize();
  const chartId = useRef(uniqid());

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;

  const pieData = useMemo(() => {
    let arcs = [
      {
        index: 0,
        value: data,
        isBackground: false
      }
    ];
    const diff = Number((1 - data).toFixed(2));

    if (data !== 0 && data % 1 !== 0) {
      arcs = arcs.concat({
        index: 1,
        value: diff,
        isBackground: true
      });
    }

    if (data === 0) {
      arcs = [
        {
          index: 1,
          value: diff,
          isBackground: true
        }
      ];
    }

    return arcs;
  }, [data]);

  const chartText = useMemo(() => {
    const texts =
      children &&
      Children.map(children, (child, index) => (
        <Fragment key={`child-${chartId.current}-${index}`}>{child}</Fragment>
      ));

    return (
      <Fragment>
        <div
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            paddingBottom: '10px'
          }}
        >
          {Number((data * 100).toFixed(2))}%
        </div>
        {texts}
      </Fragment>
    );
  }, [data, children]);

  const pieSort = (a, b) => a.index - b.index;
  const pieValue = (d) => d.value;

  return (
    <StyledDonutChartWrapper ref={chartRef}>
      <StyledDonutChartSvg width={width} height={height}>
        <svg width={width} height={height}>
          <rect rx={14} width={width} height={height} fill='#ffffff' />
          <Group
            key={`group-${chartId.current}`}
            top={centerY + margin.top}
            left={centerX + margin.left}
          >
            <Pie
              data={pieData}
              pieValue={pieValue}
              outerRadius={radius}
              innerRadius={radius - donutThickness}
              cornerRadius={25}
              pieSort={pieSort}
            >
              {(pie) => {
                return pie.arcs.map((arc, index) => {
                  return (
                    <Fragment key={`arc-${chartId.current}-${index}`}>
                      {!arc.data.isBackground && (
                        <Arc
                          startAngle={arc.startAngle}
                          endAngle={arc.endAngle}
                          innerRadius={radius - donutThickness}
                          cornerRadius={donutThickness / 2}
                          outerRadius={radius}
                          padAngle={arc.padAngle}
                          padRadius={arc.padRadius}
                          fill={color}
                        />
                      )}
                      {arc.data.isBackground && (
                        <Arc
                          startAngle={0}
                          endAngle={arc.endAngle}
                          innerRadius={radius - donutThickness}
                          cornerRadius={3}
                          outerRadius={radius}
                          padAngle={arc.padAngle}
                          padRadius={arc.padRadius}
                          fill={color}
                          opacity={0.15}
                        />
                      )}
                    </Fragment>
                  );
                });
              }}
            </Pie>
          </Group>
        </svg>
      </StyledDonutChartSvg>
      <StyledDonutChartText>{chartText}</StyledDonutChartText>
    </StyledDonutChartWrapper>
  );
};

DonutChart.propTypes = {
  donutThickness: PropTypes.number,
  margin: PropTypes.object,
  data: PropTypes.number,
  color: PropTypes.string
};

export default memo(withTheme(DonutChart));
